import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  LogOut, 
  Users, 
  Calendar,
  Mail,
  Phone,
  MapPin,
  Loader2,
  ExternalLink,
  Download,
  Search,
  Filter
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [admin, setAdmin] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    
    if (!token) {
      navigate('/admin');
      return;
    }

    setAdmin(JSON.parse(adminUser));
    fetchApplications(token);
  }, []);

  useEffect(() => {
    // Aplicar filtros
    let filtered = [...applications];

    // Filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.telefono.includes(searchTerm)
      );
    }

    // Filtro de status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Filtro de país
    if (countryFilter !== 'all') {
      filtered = filtered.filter(app => app.pais === countryFilter);
    }

    // Filtro de fecha
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(app => {
        const appDate = new Date(app.created_at);
        const diffDays = Math.floor((now - appDate) / (1000 * 60 * 60 * 24));
        
        switch(dateFilter) {
          case 'today':
            return diffDays === 0;
          case 'week':
            return diffDays <= 7;
          case 'month':
            return diffDays <= 30;
          default:
            return true;
        }
      });
    }

    setFilteredApplications(filtered);
  }, [applications, searchTerm, statusFilter, countryFilter, dateFilter]);

  const fetchApplications = async (token) => {
    try {
      const response = await axios.get(`${API}/applications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setApplications(response.data);
      setFilteredApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin');
  };

  const exportToExcel = () => {
    // Preparar datos para Excel
    const excelData = filteredApplications.map(app => ({
      'Fecha': new Date(app.created_at).toLocaleDateString('es-ES'),
      'Nombre': app.nombre_completo,
      'Email': app.email,
      'Teléfono': app.telefono,
      'País': app.pais,
      'Status': getStatusText(app.status),
      'Dedicación Actual': app.dedicacion_actual,
      'Experiencia Fútbol': app.experiencia_futbol,
      'Punto Actual': app.punto_actual_futbol,
      'Objetivo': app.objetivo_agente360,
      'Por qué Mentoría': app.por_que_mentoria,
      'Dispuesto a Invertir': app.dispuesto_invertir
    }));

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Ajustar anchos de columna
    const colWidths = [
      { wch: 12 }, // Fecha
      { wch: 25 }, // Nombre
      { wch: 30 }, // Email
      { wch: 15 }, // Teléfono
      { wch: 15 }, // País
      { wch: 12 }, // Status
      { wch: 40 }, // Dedicación
      { wch: 40 }, // Experiencia
      { wch: 40 }, // Punto Actual
      { wch: 50 }, // Objetivo
      { wch: 50 }, // Por qué
      { wch: 40 }  // Dispuesto
    ];
    ws['!cols'] = colWidths;

    // Agregar worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Aplicaciones');

    // Generar archivo
    const fileName = `agente360_aplicaciones_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const getUniqueCountries = () => {
    const countries = [...new Set(applications.map(app => app.pais))];
    return countries.sort();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      reviewed: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      scheduled: 'bg-green-500/10 text-green-500 border-green-500/20',
      rejected: 'bg-red-500/10 text-red-500 border-red-500/20'
    };
    return colors[status] || colors.pending;
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pendiente',
      reviewed: 'Revisado',
      scheduled: 'Agendado',
      rejected: 'Rechazado'
    };
    return texts[status] || 'Pendiente';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-neutral-900/50 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_futbolistas-mentor/artifacts/yf77589j_IMG_1273.png" 
                alt="Agente 360" 
                className="h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-white">Panel de Administración</h1>
                <p className="text-sm text-gray-400">{admin?.name}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={exportToExcel}
                variant="outline"
                className="border-amber-400 text-amber-400 hover:bg-amber-400/10"
              >
                <Download className="mr-2 h-4 w-4" />
                Exportar Excel
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-neutral-900 border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Aplicaciones</p>
                <p className="text-3xl font-bold text-white mt-1">{applications.length}</p>
              </div>
              <Users className="w-8 h-8 text-amber-400" />
            </div>
          </Card>

          <Card className="p-6 bg-neutral-900 border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pendientes</p>
                <p className="text-3xl font-bold text-yellow-500 mt-1">
                  {applications.filter(a => a.status === 'pending').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-yellow-500" />
            </div>
          </Card>

          <Card className="p-6 bg-neutral-900 border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Revisados</p>
                <p className="text-3xl font-bold text-blue-500 mt-1">
                  {applications.filter(a => a.status === 'reviewed').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </Card>

          <Card className="p-6 bg-neutral-900 border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Agendados</p>
                <p className="text-3xl font-bold text-green-500 mt-1">
                  {applications.filter(a => a.status === 'scheduled').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </Card>
        </div>

        {/* Filtros */}
        <Card className="p-6 bg-neutral-900 border-white/10 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-semibold text-white">Filtros</h3>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            {/* Búsqueda */}
            <div>
              <Label className="text-gray-400 text-sm mb-2">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Nombre, email o teléfono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-black border-white/10 text-white"
                />
              </div>
            </div>

            {/* Filtro por Status */}
            <div>
              <Label className="text-gray-400 text-sm mb-2">Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="all">Todos</option>
                <option value="pending">Pendientes</option>
                <option value="reviewed">Revisados</option>
                <option value="scheduled">Agendados</option>
                <option value="rejected">Rechazados</option>
              </select>
            </div>

            {/* Filtro por País */}
            <div>
              <Label className="text-gray-400 text-sm mb-2">País</Label>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="all">Todos los países</option>
                {getUniqueCountries().map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {/* Filtro por Fecha */}
            <div>
              <Label className="text-gray-400 text-sm mb-2">Fecha</Label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="all">Todas</option>
                <option value="today">Hoy</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
              </select>
            </div>
          </div>

          {/* Contador de resultados */}
          <div className="mt-4 text-sm text-gray-400">
            Mostrando {filteredApplications.length} de {applications.length} aplicaciones
          </div>
        </Card>

        {/* Applications List */}
        <Card className="p-6 bg-neutral-900 border-white/10">
          <h2 className="text-2xl font-bold text-white mb-6">Aplicaciones Recibidas</h2>
          
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                {applications.length === 0 
                  ? 'No hay aplicaciones todavía' 
                  : 'No se encontraron aplicaciones con los filtros seleccionados'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredApplications.map((app) => (
                <Card 
                  key={app.id}
                  className="p-6 bg-black border-white/10 hover:border-amber-400/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">{app.nombre_completo}</h3>
                      <p className="text-sm text-gray-400 mt-1">
                        {new Date(app.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <Badge className={getStatusColor(app.status)}>
                      {getStatusText(app.status)}
                    </Badge>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${app.email}`} className="hover:text-amber-400">
                        {app.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Phone className="w-4 h-4" />
                      <a 
                        href={`https://wa.me/${app.telefono.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-amber-400"
                      >
                        {app.telefono}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin className="w-4 h-4" />
                      {app.pais}
                    </div>
                  </div>

                  {selectedApp?.id === app.id && (
                    <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
                      <div>
                        <h4 className="font-semibold text-white mb-2">A qué se dedica:</h4>
                        <p className="text-gray-300">{app.dedicacion_actual}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-white mb-2">Experiencia en fútbol:</h4>
                        <p className="text-gray-300">{app.experiencia_futbol}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-white mb-2">Punto actual:</h4>
                        <p className="text-gray-300">{app.punto_actual_futbol}</p>
                      </div>
                      
                      <div className="bg-amber-400/10 border border-amber-400/20 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-400 mb-2">Objetivos:</h4>
                        <p className="text-gray-300 mb-3">{app.objetivo_agente360}</p>
                        
                        <h4 className="font-semibold text-amber-400 mb-2">Por qué esta mentoría:</h4>
                        <p className="text-gray-300 mb-3">{app.por_que_mentoria}</p>
                        
                        <h4 className="font-semibold text-amber-400 mb-2">Dispuesto a invertir:</h4>
                        <p className="text-white font-semibold">{app.dispuesto_invertir}</p>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
